export type MicrophoneErrorReason =
  | 'unsupported'
  | 'notFound'
  | 'permissionDenied'
  | 'inUse'
  | 'unknown';

/**
 * Keys live in the common namespace because both the conversation composer and the consultation
 * voice field need them.
 */
const MICROPHONE_ERROR_KEYS: Record<MicrophoneErrorReason, string> = {
  unsupported: 'microphoneUnsupported',
  notFound: 'microphoneNotFound',
  permissionDenied: 'microphonePermissionDenied',
  inUse: 'microphoneInUse',
  unknown: 'microphoneUnavailable',
};

export class MicrophoneError extends Error {
  readonly reason: MicrophoneErrorReason;

  constructor(reason: MicrophoneErrorReason, cause?: unknown) {
    super(`Microphone unavailable: ${reason}`);
    this.name = 'MicrophoneError';
    this.reason = reason;
    this.cause = cause;
  }
}

/**
 * Browsers report microphone trouble as a DOMException whose message is written for developers:
 * a missing device comes back as "Requested device not found", which tells a user nothing.
 */
export const resolveMicrophoneErrorReason = (error: unknown): MicrophoneErrorReason => {
  if (error instanceof MicrophoneError) {
    return error.reason;
  }

  switch ((error as { name?: string } | null | undefined)?.name) {
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return 'notFound';
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'permissionDenied';
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'inUse';
    default:
      return 'unknown';
  }
};

export const getMicrophoneErrorKey = (error: unknown): string =>
  MICROPHONE_ERROR_KEYS[resolveMicrophoneErrorReason(error)];

export const isMicrophoneSupported = (): boolean => Boolean(navigator.mediaDevices?.getUserMedia);

/**
 * Always throws a {@link MicrophoneError} so callers can translate the outcome instead of leaking
 * the browser's own wording.
 */
export const requestMicrophoneStream = async (
  constraints?: MediaTrackConstraints,
): Promise<MediaStream> => {
  if (!isMicrophoneSupported()) {
    throw new MicrophoneError('unsupported');
  }

  try {
    return await navigator.mediaDevices.getUserMedia({ audio: constraints ?? true });
  } catch (error) {
    const reason = resolveMicrophoneErrorReason(error);

    // Processing hints such as echo cancellation are only preferences, but a browser that treats
    // them as requirements would reject a device that plain audio can still open.
    if (constraints && reason === 'notFound') {
      try {
        return await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (fallbackError) {
        throw new MicrophoneError(resolveMicrophoneErrorReason(fallbackError), fallbackError);
      }
    }

    throw new MicrophoneError(reason, error);
  }
};
